import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { recordarIdioma } from '../i18n/idioma';
import { api, urlDeFoto, type Profile } from '../api/client';
import { Modal } from '../components/Modal';
import { useSession } from '../session/SessionContext';

/**
 * Mi perfil.
 *
 * Contiene lo que es del portal —foto, idioma, qué grupos y qué rol tengo— y
 * **la contraseña se cambia desde aquí sin salir del portal**: el botón lleva al
 * servidor de identidad con la acción ya puesta, muestra su pantalla con el tema
 * de la caja y regresa solo. El portal no puede ver una contraseña (ADR-0002),
 * pero eso no obliga a soltar a la persona en la consola de cuenta de Keycloak,
 * que se ve como otro sistema y de la que no se vuelve.
 *
 * Las iniciales cuando no hay foto no son un adorno: sin ellas, la ausencia de
 * foto se ve como un hueco roto en vez de como algo opcional.
 */
export function Perfil() {
  const { t } = useTranslation();
  const { estado } = useSession();
  // Quien sólo consulta no lleva segundo factor: enseñarle su tarjeta es
  // ofrecerle administrar algo que no tiene.
  const usaSegundoFactor = estado.status === 'autenticado' && estado.session.mfaRequired;
  const [perfil, setPerfil] = useState<Profile | null>(null);
  // Los nombres de los idiomas salen de la base, que es donde están declarados.
  // Codificarlos aquí obligaría a tocar la interfaz para agregar un idioma, y
  // además cada uno se escribe en su propia lengua: «Español» no se traduce.
  const [idiomas, setIdiomas] = useState<{ code: string; name: string }[]>([]);
  const [confirmandoMfa, setConfirmandoMfa] = useState(false);
  const [mfaListo, setMfaListo] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [guardado, setGuardado] = useState(false);

  const cargar = useCallback(async () => {
    try {
      setPerfil(await api.profile());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    void cargar();
    api.locales().then(setIdiomas).catch(() => setIdiomas([]));
  }, [cargar]);

  const cambiarIdioma = async (locale: string) => {
    setError(null);
    try {
      setPerfil(await api.updateProfile(locale));
      await i18n.changeLanguage(locale);
      recordarIdioma(locale);
      setGuardado(true);
      window.setTimeout(() => setGuardado(false), 2500);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  if (!perfil) {
    return (
      <div className="pagina__encabezado">
        <h1 className="pagina__titulo">{t('profile.title')}</h1>
        {error && (
          <p className="aviso aviso--error" role="alert">
            {error}
          </p>
        )}
      </div>
    );
  }

  const foto = urlDeFoto(perfil.id, perfil.photoUpdatedAt);
  const iniciales = perfil.fullName
    .split(/\s+/)
    .slice(0, 2)
    .map((parte) => parte.charAt(0).toUpperCase())
    .join('');

  return (
    <>
      <div className="pagina__encabezado">
        <h1 className="pagina__titulo">{t('profile.title')}</h1>
        <p className="pagina__bajada">{t('profile.explanation')}</p>
      </div>

      {error && (
        <p className="aviso aviso--error" role="alert">
          {error}
        </p>
      )}
      {guardado && <p className="aviso aviso--exito">{t('profile.saved')}</p>}
      {mfaListo && <p className="aviso aviso--exito">{t('profile.mfaDone')}</p>}

      <section className="tarjeta">
        <div className="tarjeta__encabezado">
          <h2 className="tarjeta__titulo">{t('profile.photoTitle')}</h2>
        </div>

        <div className="perfil__foto-fila">
          {foto ? (
            <img className="perfil__foto" src={foto} alt={perfil.fullName} />
          ) : (
            <span className="perfil__iniciales" aria-hidden="true">
              {iniciales}
            </span>
          )}

          {/* La pone un administrador, general o local; aquí sólo se ve. */}
          <p className="campo__ayuda">{t('photo.assignedByAdmin')}</p>
        </div>
      </section>

      <section className="tarjeta">
        <div className="tarjeta__encabezado">
          <h2 className="tarjeta__titulo">{t('profile.dataTitle')}</h2>
        </div>
        <dl className="datos">
          <div className="dato">
            <dt className="dato__etiqueta">{t('profile.fullName')}</dt>
            <dd>{perfil.fullName}</dd>
          </div>
          <div className="dato">
            <dt className="dato__etiqueta">{t('profile.username')}</dt>
            <dd className="mono">{perfil.username}</dd>
          </div>
          <div className="dato">
            <dt className="dato__etiqueta">{t('profile.email')}</dt>
            <dd>{perfil.email}</dd>
          </div>
          <div className="dato">
            <dt className="dato__etiqueta">{t('profile.myRoles')}</dt>
            <dd>{perfil.roles.join(', ')}</dd>
          </div>
          <div className="dato">
            <dt className="dato__etiqueta">{t('profile.lastLogin')}</dt>
            <dd>
              {perfil.lastLoginAt
                ? new Date(perfil.lastLoginAt).toLocaleString(i18n.language)
                : t('profile.never')}
            </dd>
          </div>
        </dl>

        <h3 className="modal__seccion">{t('profile.myGroups')}</h3>
        {perfil.groups.length === 0 ? (
          <p className="tenue">{t('profile.noGroups')}</p>
        ) : (
          <ul className="lista-etiquetas">
            {perfil.groups.map((grupo) => (
              <li key={grupo.id}>{grupo.name}</li>
            ))}
          </ul>
        )}
      </section>

      <section className="tarjeta">
        <div className="tarjeta__encabezado">
          <h2 className="tarjeta__titulo">{t('profile.languageTitle')}</h2>
        </div>
        <div className="campo">
          <label htmlFor="perfil-idioma">{t('profile.languageHint')}</label>
          <select
            id="perfil-idioma"
            value={perfil.preferredLocale ?? 'es-MX'}
            onChange={(e) => void cambiarIdioma(e.target.value)}
          >
            {idiomas.map((idioma) => (
              <option key={idioma.code} value={idioma.code}>
                {idioma.name}
              </option>
            ))}
          </select>
        </div>
      </section>

      {usaSegundoFactor && (
      <section className="tarjeta">
        <div className="tarjeta__encabezado">
          <h2 className="tarjeta__titulo">{t('profile.mfaTitle')}</h2>
        </div>
        <p>{t('profile.mfaHint')}</p>

        <p className="campo__ayuda">{t('profile.mfaChangeHint')}</p>
        <p>
          <button className="boton" type="button" onClick={() => setConfirmandoMfa(true)}>
            {t('profile.mfaChange')}
          </button>
        </p>

        <h3 className="modal__seccion">{t('profile.mfaLostTitle')}</h3>
        <p className="campo__ayuda">{t('profile.mfaLostHint')}</p>
      </section>
      )}

      <section className="tarjeta">
        <div className="tarjeta__encabezado">
          <h2 className="tarjeta__titulo">{t('profile.passwordTitle')}</h2>
        </div>
        <p>{t('profile.passwordHint')}</p>
        <p>
          {/* Enlace y no botón: es una navegación al servidor de identidad, con
              vuelta por el callback de siempre. Un fetch no podría hacerlo. */}
          <a className="boton boton--fantasma" href="/api/v1/auth/password/change?returnTo=/perfil">
            {t('profile.passwordAction')}
          </a>
        </p>
      </section>

      {confirmandoMfa && (
        <Modal titulo={t('profile.mfaConfirm')} onCerrar={() => setConfirmandoMfa(false)}>
          <p>{t('profile.mfaConfirmHint')}</p>
          <div className="modal__acciones">
            <button
              className="boton boton--fantasma"
              type="button"
              onClick={() => setConfirmandoMfa(false)}
            >
              {t('groups.cancel')}
            </button>
            <button
              className="boton"
              type="button"
              onClick={async () => {
                try {
                  await api.resetMySecondFactor();
                  setConfirmandoMfa(false);
                  setMfaListo(true);
                } catch (e) {
                  setConfirmandoMfa(false);
                  setError(e instanceof Error ? e.message : String(e));
                }
              }}
            >
              {t('profile.mfaConfirmAction')}
            </button>
          </div>
        </Modal>
      )}
    </>
  );
}
